import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import {
  AdminBook,
  BookAdminApi,
  BookAdminPayload,
  BookAdminSearch,
  BookLoanHistory,
  BookLoanHistoryStatus,
  BookSection,
  bookSectionLabels,
} from "../../interface/book-admin-api";
import BookBarcodeScannerDialog from "../components/BookBarcodeScannerDialog";
import { isValidEan13 } from "../../utils/book-barcode";

const bookAdminApi = new BookAdminApi();
const sections = Object.keys(bookSectionLabels) as BookSection[];

const emptyBook = (): BookAdminPayload => ({
  title: "",
  author: "",
  barcode: "",
  image: "",
  description: "",
  maincopy: "",
  tag: "",
  badge: "",
  url: "",
  section: "literature",
  isRecommended: false,
  recommendationOrder: 0,
});

function isBook(value: unknown): value is AdminBook {
  return Boolean(value && typeof value === "object" && typeof (value as AdminBook).sk === "string" && typeof (value as AdminBook).title === "string");
}

function normaliseBook(value: AdminBook): AdminBook {
  return {
    ...emptyBook(),
    ...value,
    section: sections.includes(value.section) ? value.section : "literature",
    isRecommended: Boolean(value.isRecommended),
    recommendationOrder: Number(value.recommendationOrder || 0),
  };
}

function getCatalogErrorMessage(response: { response?: { error_msg?: unknown } } | undefined) {
  const errorMessage = response?.response?.error_msg;
  if (errorMessage === "Missing Authentication Token") {
    return "도서 관리 API가 아직 운영 게이트웨이에 배포되지 않았습니다. 배포 후 다시 시도해 주세요.";
  }
  return "도서 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

function isLoanHistory(value: unknown): value is BookLoanHistory {
  const loan = value as Partial<BookLoanHistory> | undefined;
  return Boolean(
    loan
    && typeof loan.loanId === "string"
    && typeof loan.bookSk === "string"
    && typeof loan.title === "string"
    && typeof loan.barcode === "string"
    && typeof loan.borrowedAt === "string",
  );
}

function formatDateTime(value?: string) {
  if (!value) return "-";
  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return matched ? `${matched[1]}.${matched[2]}.${matched[3]} ${matched[4]}:${matched[5]}` : value;
}

function formatAddress(dong?: string, ho?: string) {
  if (!dong || !ho) return "-";
  return `${dong}동 ${ho.padStart(4, "0")}호`;
}

function formatReturner(returnedBy?: BookLoanHistory["returnedBy"]) {
  return returnedBy || "-";
}

const BookAdmin = () => {
  const [books, setBooks] = useState<AdminBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<AdminBook | null>(null);
  const [form, setForm] = useState<BookAdminPayload>(emptyBook);
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState<BookAdminSearch>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [loanStatus, setLoanStatus] = useState<BookLoanHistoryStatus>("ACTIVE");
  const [loans, setLoans] = useState<BookLoanHistory[]>([]);
  const [loansLoading, setLoansLoading] = useState(false);
  const [loanMessage, setLoanMessage] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);

  const searchCriteria = useMemo(
    () => Object.fromEntries(
      Object.entries(search).flatMap(([field, value]) => {
        const trimmed = value?.trim();
        return trimmed ? [[field, trimmed]] : [];
      }),
    ) as BookAdminSearch,
    [search],
  );

  const loadBooks = useCallback(async () => {
    if (!Object.keys(searchCriteria).length) {
      setBooks([]);
      setHasSearched(false);
      setMessage("SK, 제목, 저자 또는 바코드 중 하나를 입력하고 검색해 주세요.");
      return;
    }
    setLoading(true);
    setMessage("");
    setHasSearched(true);
    const response = await bookAdminApi.get_book_admin_catalog(searchCriteria);
    const catalog = response?.response?.result;
    const result = Array.isArray(catalog) ? catalog : catalog?.books;
    if (response?.code !== "200" || !Array.isArray(result)) {
      setBooks([]);
      setMessage(getCatalogErrorMessage(response));
      setLoading(false);
      return;
    }

    setBooks(result.filter(isBook).map(normaliseBook));
    setLoading(false);
  }, [searchCriteria]);

  const loadLoanHistory = useCallback(async (status: BookLoanHistoryStatus) => {
    setLoansLoading(true);
    setLoanMessage("");
    const response = await bookAdminApi.get_book_loan_history({ status });
    const result = response?.response?.result;
    if (response?.code !== "200" || !Array.isArray(result)) {
      setLoans([]);
      setLoanMessage("대여 현황을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
      setLoansLoading(false);
      return;
    }
    setLoans(result.filter(isLoanHistory));
    setLoansLoading(false);
  }, []);

  useEffect(() => {
    void loadLoanHistory("ACTIVE");
  }, [loadLoanHistory]);

  const handleBarcodeScan = useCallback((barcode: string) => {
    setForm((current) => ({ ...current, barcode }));
    setScannerOpen(false);
  }, []);

  const dialogTitle = editingBook ? "도서 정보 수정" : "도서 등록";
  const orderedBooks = useMemo(
    () => [...books].sort((a, b) => a.title.localeCompare(b.title, "ko")),
    [books],
  );

  const openCreate = () => {
    setEditingBook(null);
    setForm(emptyBook());
    setFormError("");
    setMessage("");
    setDialogOpen(true);
  };

  const openEdit = (book: AdminBook) => {
    setEditingBook(book);
    const { pk: _pk, sk: _sk, version: _version, isNew: _isNew, ...editable } = normaliseBook(book);
    setForm(editable);
    setFormError("");
    setMessage("");
    setDialogOpen(true);
  };

  const setTextField = (field: keyof BookAdminPayload, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setFormError("책 제목을 입력해 주세요.");
      return;
    }
    const barcode = form.barcode?.trim() || "";
    if (barcode && !isValidEan13(barcode)) {
      setFormError("바코드 13자리와 체크숫자를 확인해 주세요.");
      return;
    }
    setSaving(true);
    setFormError("");

    const response = editingBook
      ? await bookAdminApi.update_book_admin({ ...editingBook, ...form, barcode })
      : await bookAdminApi.create_book_admin({ ...form, barcode });
    if (response?.code !== "200") {
      setFormError(response?.response?.error_msg === "barcodeAlreadyExists"
        ? "이미 다른 도서에 등록된 바코드입니다."
        : editingBook
          ? "도서 정보를 저장하지 못했습니다. 최신 목록을 확인해 주세요."
          : "도서를 등록하지 못했습니다.");
      setSaving(false);
      return;
    }

    setDialogOpen(false);
    setSaving(false);
    if (Object.keys(searchCriteria).length) await loadBooks();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1180, mx: "auto" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2} mb={3}>
        <Box>
          <Typography component="h1" variant="h5" fontWeight={700}>도서 등록</Typography>
          <Typography variant="body2" color="text.secondary">새 도서는 저장 즉시 신착 도서 목록에 등록됩니다.</Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            label="SK"
            size="small"
            value={search.sk || ""}
            onChange={(event) => setSearch((current) => ({ ...current, sk: event.target.value }))}
            sx={{ minWidth: { xs: 0, sm: 150 } }}
          />
          <TextField
            label="제목"
            size="small"
            value={search.title || ""}
            onChange={(event) => setSearch((current) => ({ ...current, title: event.target.value }))}
            sx={{ minWidth: { xs: 0, sm: 150 } }}
          />
          <TextField
            label="저자"
            size="small"
            value={search.author || ""}
            onChange={(event) => setSearch((current) => ({ ...current, author: event.target.value }))}
            sx={{ minWidth: { xs: 0, sm: 150 } }}
          />
          <TextField
            label="바코드"
            size="small"
            value={search.barcode || ""}
            onChange={(event) => setSearch((current) => ({ ...current, barcode: event.target.value.replace(/\D/g, "").slice(0, 13) }))}
            inputProps={{ inputMode: "numeric", maxLength: 13 }}
            sx={{ minWidth: { xs: 0, sm: 170 } }}
          />
          <Button variant="outlined" onClick={() => void loadBooks()} disabled={loading}>검색</Button>
          <Button variant="contained" onClick={openCreate} startIcon={<AddIcon />}>도서 등록</Button>
        </Stack>
      </Stack>

      {message ? <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert> : null}

      <Box sx={{ overflowX: "auto", bgcolor: "background.paper", borderRadius: 1 }}>
        <Table size="small" aria-label="도서 관리 목록">
          <TableHead>
            <TableRow>
              <TableCell>도서 / SK</TableCell>
              <TableCell>섹션</TableCell>
              <TableCell>추천</TableCell>
              <TableCell>신착</TableCell>
              <TableCell align="right">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5}>도서 목록을 불러오는 중입니다.</TableCell></TableRow> : null}
            {!loading && !hasSearched ? <TableRow><TableCell colSpan={5}>SK, 제목, 저자 또는 바코드 중 하나를 입력하고 검색해 주세요.</TableCell></TableRow> : null}
            {!loading && hasSearched && !orderedBooks.length ? <TableRow><TableCell colSpan={5}>검색 결과가 없습니다.</TableCell></TableRow> : null}
            {orderedBooks.map((book) => (
              <TableRow key={book.sk} hover>
                <TableCell>
                  <Typography fontWeight={600}>{book.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{book.author} · {book.sk}</Typography>
                  {book.barcode ? <Typography display="block" variant="caption" color="text.secondary">바코드 {book.barcode}</Typography> : null}
                </TableCell>
                <TableCell>{bookSectionLabels[book.section]}</TableCell>
                <TableCell>{book.isRecommended ? `노출 ${book.recommendationOrder || ""}` : "-"}</TableCell>
                <TableCell>{book.isNew ? "신착" : "-"}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => openEdit(book)} startIcon={<EditIcon />}>수정</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} mb={2}>
          <Box>
            <Typography component="h2" variant="h6" fontWeight={700}>대여 현황</Typography>
            <Typography variant="body2" color="text.secondary">현재 대여중인 도서와 반납 완료 이력을 확인합니다.</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {(["ACTIVE", "ALL"] as BookLoanHistoryStatus[]).map((status) => (
              <Button
                key={status}
                variant={loanStatus === status ? "contained" : "outlined"}
                onClick={() => {
                  setLoanStatus(status);
                  void loadLoanHistory(status);
                }}
                disabled={loansLoading}
              >
                {status === "ACTIVE" ? "현재 대여중" : "전체 이력"}
              </Button>
            ))}
          </Stack>
        </Stack>
        {loanMessage ? <Alert severity="error" sx={{ mb: 2 }}>{loanMessage}</Alert> : null}
        <Box sx={{ overflowX: "auto", bgcolor: "background.paper", borderRadius: 1 }}>
          <Table size="small" aria-label="도서 대여 현황">
            <TableHead>
              <TableRow>
                <TableCell>도서 / 바코드</TableCell>
                <TableCell>대여일</TableCell>
                <TableCell>동·호</TableCell>
                <TableCell>반납일</TableCell>
                <TableCell>반납처리자</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loansLoading ? <TableRow><TableCell colSpan={5}>대여 현황을 불러오는 중입니다.</TableCell></TableRow> : null}
              {!loansLoading && !loans.length ? <TableRow><TableCell colSpan={5}>{loanStatus === "ACTIVE" ? "현재 대여중인 도서가 없습니다." : "대여 이력이 없습니다."}</TableCell></TableRow> : null}
              {!loansLoading && loans.map((loan) => (
                <TableRow key={loan.loanId} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{loan.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{loan.barcode}</Typography>
                  </TableCell>
                  <TableCell>{formatDateTime(loan.borrowedAt)}</TableCell>
                  <TableCell>{formatAddress(loan.borrowerDong, loan.borrowerHo)}</TableCell>
                  <TableCell>{loan.returnedAt ? formatDateTime(loan.returnedAt) : "대여중"}</TableCell>
                  <TableCell>{formatReturner(loan.returnedBy)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {formError ? <Alert severity="error">{formError}</Alert> : null}
              <TextField required label="제목" value={form.title} onChange={(event) => setTextField("title", event.target.value)} autoFocus />
              <TextField label="저자" value={form.author} onChange={(event) => setTextField("author", event.target.value)} />
              <TextField
                label="바코드"
                value={form.barcode || ""}
                onChange={(event) => setTextField("barcode", event.target.value.replace(/\D/g, "").slice(0, 13))}
                inputProps={{ inputMode: "numeric", maxLength: 13 }}
                helperText="책 뒷면의 ISBN/EAN-13 숫자 13자리를 입력해 주세요."
              />
              <Button
                type="button"
                variant="outlined"
                startIcon={<QrCodeScannerRoundedIcon />}
                onClick={() => setScannerOpen(true)}
              >
                바코드 스캔
              </Button>
              <FormControl>
                <InputLabel id="book-section-label">섹션</InputLabel>
                <Select labelId="book-section-label" label="섹션" value={form.section} onChange={(event) => setForm((current) => ({ ...current, section: event.target.value as BookSection }))}>
                  {sections.map((section) => <MenuItem key={section} value={section}>{bookSectionLabels[section]}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="표지 URL" type="url" value={form.image} onChange={(event) => setTextField("image", event.target.value)} />
              <TextField label="상세 URL" type="url" value={form.url} onChange={(event) => setTextField("url", event.target.value)} />
              <TextField label="태그" value={form.tag} onChange={(event) => setTextField("tag", event.target.value)} />
              <TextField label="배지" value={form.badge} onChange={(event) => setTextField("badge", event.target.value)} />
              <TextField label="메인카피" value={form.maincopy} onChange={(event) => setTextField("maincopy", event.target.value)} multiline minRows={2} />
              <TextField label="도서 소개" value={form.description} onChange={(event) => setTextField("description", event.target.value)} multiline minRows={4} />
              <FormControlLabel control={<Checkbox checked={Boolean(form.isRecommended)} onChange={(event) => setForm((current) => ({ ...current, isRecommended: event.target.checked }))} />} label="추천도서에 노출" />
              <TextField label="추천 순서" type="number" inputProps={{ min: 0 }} disabled={!form.isRecommended} value={form.recommendationOrder} onChange={(event) => setForm((current) => ({ ...current, recommendationOrder: Number(event.target.value) || 0 }))} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>취소</Button>
            <Button type="submit" variant="contained" disabled={saving}>{saving ? "저장 중" : "저장"}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {scannerOpen ? <BookBarcodeScannerDialog onScan={handleBarcodeScan} onClose={() => setScannerOpen(false)} /> : null}
    </Box>
  );
};

export default BookAdmin;
